/**
 * estimatePreFill.js
 *
 * Shared "convert estimate to sale" hand-off used by both the View Estimates
 * screen and the Estimate creation screens (INREstimate/USDEstimate) "Convert
 * to Billing" action. Builds the payload consumed by IndianBilling.js /
 * USBilling.js on mount (window.__estimatePreFill) and navigates there via
 * the same custom 'navigate' event the rest of the app uses.
 */

/**
 * @param {Object} estimate - an estimate record (from GET/POST /api/estimates),
 *   must include at least: currency, customer_name/phone/email, item_code,
 *   gross_weight, net_weight, gold_purity, gold_rate, wastage_percent,
 *   making_charge_per_gram, tax_rate, stone_details, estimate_number, id.
 * @param {Object} [overrides] - optional rate overrides (goldRate, wastage, making, customsPct, discount)
 */
export const buildEstimatePreFill = (estimate, overrides = {}) => {
  // Parse stone_details for billing pre-fill. stone_details is JSONB and
  // normally comes back already parsed, but has been observed as a raw JSON
  // string too, so parse defensively either way.
  let rawStoneDetails = estimate.stone_details;
  if (typeof rawStoneDetails === 'string') {
    try { rawStoneDetails = JSON.parse(rawStoneDetails); } catch { rawStoneDetails = []; }
  }
  // Remap from the saved shape ({ code, name, rate }, written by
  // saveEstimate()) to the raw API shape ({ stone_code, stone_name,
  // sale_price }) that IndianBilling.js/USBilling.js's StoneSection expects
  // for its initialStones prop — without this, rates/codes silently come
  // through as 0/blank on every "Convert to Billing".
  const stoneDetails = Array.isArray(rawStoneDetails)
    ? rawStoneDetails.map(s => ({
        stone_code: s.stone_code || s.code || '',
        stone_name: s.stone_name || s.name || '',
        weight: s.weight,
        unit: s.unit || 'gram',
        sale_price: s.sale_price ?? s.rate ?? 0,
        cost_price: s.cost_price ?? s.purchaseRate ?? 0,
      }))
    : [];

  // Back-calculate 24K rate from the stored purity-adjusted rate
  // estimate.gold_rate is the purity-adjusted rate (e.g. 22K rate), so:
  // gold_rate_24k = gold_rate / (purity / 24)
  const purity = parseFloat(estimate.gold_purity) || 24;
  const storedRate = parseFloat(estimate.gold_rate) || 0;
  const goldRate24k = purity > 0 && storedRate > 0
    ? (storedRate / (purity / 24)).toFixed(2)
    : '';

  return {
    estimateNumber: estimate.estimate_number,
    estimateId: estimate.id,
    customerName: estimate.customer_name || '',
    customerPhone: estimate.customer_phone || '',
    customerEmail: estimate.customer_email || '',
    itemCode: estimate.item_code || '',
    grossWeight: estimate.gross_weight || '',
    netWeight: estimate.net_weight || '',
    purity: estimate.gold_purity || '',
    goldRate: overrides.goldRate ?? goldRate24k,
    wastage: overrides.wastage ?? estimate.wastage_percent ?? '',
    making: overrides.making ?? estimate.making_charge_per_gram ?? '',
    customsPct: overrides.customsPct ?? estimate.tax_rate ?? '',
    discount: overrides.discount ?? 0,
    stoneDetails,
    currency: estimate.currency,
  };
};

/**
 * Stores the pre-fill payload and navigates to the matching billing module
 * (indian-billing for INR, us-billing for USD) via the app's custom
 * 'navigate' event.
 *
 * @param {Object} estimate
 * @param {Object} [overrides]
 */
export const convertEstimateToBilling = (estimate, overrides = {}) => {
  const targetModule = estimate.currency === 'USD' ? 'us-billing' : 'indian-billing';
  window.__estimatePreFill = buildEstimatePreFill(estimate, overrides);
  window.dispatchEvent(new CustomEvent('navigate', { detail: targetModule }));
};
